import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AdminService } from "../api/AdminService";

// Matches RegulifyAIProductModuleNames in api-regulify/app/core/common/utilities.py
const MODULE_NAMES = ["BOT", "PDD", "DCR", "RMG", "CLI", "CLE", "ALL"];

const emptyCreateForm = {
  email: "",
  first_name: "",
  last_name: "",
  phone: "",
  temporary_password: "",
  module_names: [],
  total_credits: 1000,
};

const AdminClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [creating, setCreating] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);
  const [editValues, setEditValues] = useState({ total_credits: 0, module_names: [] });
  const [savingEdit, setSavingEdit] = useState(false);
  const [usageEmail, setUsageEmail] = useState(null);
  const [usageRows, setUsageRows] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);

  const loadClients = async () => {
    setLoading(true);
    try {
      const data = await AdminService.listClients();
      setClients(data);
    } catch (error) {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  const toggleModule = (formSetter, current, name) => {
    const has = current.includes(name);
    formSetter(has ? current.filter((m) => m !== name) : [...current, name]);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.email || !createForm.temporary_password) {
      toast.warning("Email and a temporary password are required");
      return;
    }
    setCreating(true);
    try {
      const result = await AdminService.createClient({
        ...createForm,
        module_names: createForm.module_names.join(","),
      });
      if (result.action === "approved") {
        toast.success(
          `${createForm.email} had already self-registered via the app — approved their existing account and granted access. They can log in with the password they already set (no need to share the temporary password).`,
        );
      } else {
        toast.success(
          `Client created. Share the temporary password with ${createForm.email} manually — Cognito will force a password change on first login.`,
        );
      }
      setShowCreate(false);
      setCreateForm(emptyCreateForm);
      loadClients();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Failed to create client",
      );
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (client) => {
    setEditingEmail(client.email_id);
    setEditValues({
      total_credits: client.total_credits,
      module_names: client.module_names ? client.module_names.split(",") : [],
    });
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await AdminService.updateClientCredits(
        editingEmail,
        Number(editValues.total_credits),
        editValues.module_names.join(","),
      );
      toast.success("Client updated");
      setEditingEmail(null);
      loadClients();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || "Failed to update client",
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const viewUsage = async (email) => {
    setUsageEmail(email);
    setUsageLoading(true);
    try {
      const rows = await AdminService.getClientUsage(email);
      setUsageRows(rows);
    } catch (error) {
      toast.error("Failed to load usage history");
    } finally {
      setUsageLoading(false);
    }
  };

  return (
    <div className="p-4">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-bold">Clients</h2>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => setShowCreate(true)}
        >
          + Create Client
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="table table-zebra">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Modules</th>
                <th>Credits (used / total)</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => (
                <tr key={client.email_id}>
                  <td>{client.email_id}</td>
                  <td>
                    {client.first_name} {client.last_name}
                  </td>
                  <td>
                    {editingEmail === client.email_id ? (
                      <div className="flex flex-wrap gap-1">
                        {MODULE_NAMES.map((name) => (
                          <label key={name} className="label cursor-pointer gap-1">
                            <input
                              type="checkbox"
                              className="checkbox checkbox-xs"
                              checked={editValues.module_names.includes(name)}
                              onChange={() =>
                                toggleModule(
                                  (v) =>
                                    setEditValues((prev) => ({ ...prev, module_names: v })),
                                  editValues.module_names,
                                  name,
                                )
                              }
                            />
                            <span className="text-xs">{name}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      client.module_names
                    )}
                  </td>
                  <td>
                    {editingEmail === client.email_id ? (
                      <input
                        type="number"
                        className="input input-bordered input-sm w-28"
                        value={editValues.total_credits}
                        onChange={(e) =>
                          setEditValues((prev) => ({
                            ...prev,
                            total_credits: e.target.value,
                          }))
                        }
                      />
                    ) : (
                      `${client.credits_used} / ${client.total_credits}`
                    )}
                  </td>
                  <td>{new Date(client.created_at).toLocaleDateString()}</td>
                  <td className="flex gap-1">
                    {editingEmail === client.email_id ? (
                      <>
                        <button
                          className="btn btn-success btn-xs"
                          disabled={savingEdit}
                          onClick={saveEdit}
                        >
                          Save
                        </button>
                        <button
                          className="btn btn-ghost btn-xs"
                          onClick={() => setEditingEmail(null)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="btn btn-outline btn-xs"
                          onClick={() => startEdit(client)}
                        >
                          Edit Credits
                        </button>
                        <button
                          className="btn btn-ghost btn-xs"
                          onClick={() => viewUsage(client.email_id)}
                        >
                          Usage
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <div className="modal modal-open">
          <div className="modal-box max-w-lg">
            <h3 className="text-lg font-bold">Create Client</h3>
            <p className="py-1 text-sm text-base-content/70">
              Creates a standard (non-admin) Cognito account with a verified
              email. You set the temporary password and communicate it to the
              client manually — Cognito does not send an invite email.
            </p>
            <p className="py-1 text-sm text-base-content/70">
              If this email already self-registered via the app&apos;s own
              Register page (pending admin approval), this instead approves
              their existing account and grants access — the temporary
              password below is ignored in that case, since they already set
              their own.
            </p>
            <form onSubmit={handleCreate} className="flex flex-col gap-2">
              <input
                type="email"
                placeholder="Email"
                required
                className="input input-bordered w-full"
                value={createForm.email}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, email: e.target.value }))
                }
              />
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="First name"
                  className="input input-bordered w-full"
                  value={createForm.first_name}
                  onChange={(e) =>
                    setCreateForm((prev) => ({ ...prev, first_name: e.target.value }))
                  }
                />
                <input
                  type="text"
                  placeholder="Last name"
                  className="input input-bordered w-full"
                  value={createForm.last_name}
                  onChange={(e) =>
                    setCreateForm((prev) => ({ ...prev, last_name: e.target.value }))
                  }
                />
              </div>
              <input
                type="tel"
                placeholder="Phone"
                className="input input-bordered w-full"
                value={createForm.phone}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, phone: e.target.value }))
                }
              />
              <input
                type="text"
                placeholder="Temporary password"
                required
                className="input input-bordered w-full"
                value={createForm.temporary_password}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    temporary_password: e.target.value,
                  }))
                }
              />
              <input
                type="number"
                placeholder="Total credits"
                className="input input-bordered w-full"
                value={createForm.total_credits}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    total_credits: e.target.value,
                  }))
                }
              />
              <div className="flex flex-wrap gap-2">
                {MODULE_NAMES.map((name) => (
                  <label key={name} className="label cursor-pointer gap-1">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm"
                      checked={createForm.module_names.includes(name)}
                      onChange={() =>
                        toggleModule(
                          (v) => setCreateForm((prev) => ({ ...prev, module_names: v })),
                          createForm.module_names,
                          name,
                        )
                      }
                    />
                    <span className="text-sm">{name}</span>
                  </label>
                ))}
              </div>
              <div className="modal-action">
                <button
                  type="button"
                  className="btn"
                  onClick={() => setShowCreate(false)}
                  disabled={creating}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? "Creating..." : "Create"}
                </button>
              </div>
            </form>
          </div>
          <div className="modal-backdrop" onClick={() => setShowCreate(false)}></div>
        </div>
      )}

      {usageEmail && (
        <div className="modal modal-open">
          <div className="modal-box max-w-2xl">
            <h3 className="text-lg font-bold">Usage history — {usageEmail}</h3>
            {usageLoading ? (
              <p>Loading...</p>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <table className="table table-zebra table-sm">
                  <thead>
                    <tr>
                      <th>Module</th>
                      <th>Method</th>
                      <th>Credits</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usageRows.map((row, idx) => (
                      <tr key={idx}>
                        <td>{row.module_name}</td>
                        <td>{row.method_name}</td>
                        <td>{row.used_credit}</td>
                        <td>{new Date(row.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="modal-action">
              <button className="btn" onClick={() => setUsageEmail(null)}>
                Close
              </button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => setUsageEmail(null)}></div>
        </div>
      )}
    </div>
  );
};

export default AdminClientsPage;
